import React, { useEffect, useState } from "react";
import { getDataWithFilterUrl } from "../../controllers/APIController";
import {
  Table,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  TableColumn,
  Button,
  useDisclosure,
  Image,
  Pagination,
  Card,
  CardBody,
  Chip,
  Badge,
} from "@nextui-org/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDownload,
  faEye,
  faFileAlt,
  faCalendar,
  faUser,
  faBuilding,
  faCheckCircle,
  faExclamationTriangle,
  faClock,
  faPlus,
  faFilter,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import DownloadIcon from "../../Images/Icons/download.svg";

import BreadCrumbsPage from "../../components/BreadCrumbsPage";
import NullData from "../../components/ErrorPage/NullData";
import LoadingPage from "../../components/Loader/LoadingPage";
import AddEvidenceModal from "./AddEvidenceModal";
import { StandardPrimaryButton, StandardSecondaryButton } from "../../components/StandardButtons";

const AuditReports = () => {
  const [reRenderPage, setReRenderPage] = useState(true);
  const [filterEvidenceData, setFilterEvidenceData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const { isOpen, onOpen, onOpenChange } = useDisclosure();

  const UserLogged = JSON.parse(sessionStorage.getItem("userData"));
  const OrgInfo = UserLogged && UserLogged.user.LastOrgId;
  const orgFilterUrl = "filters[Organization][id][$eqi]=" + OrgInfo;

  // Mock data for audit reports
  const mockAuditReports = [
    {
      id: 1,
      name: "2024 ESG Compliance Audit Report",
      description: "Comprehensive audit report covering environmental, social, and governance compliance for FY 2024",
      referenceTo: "ESG Framework v2.1",
      uploadDate: "2024-12-15",
      status: "approved",
      auditor: "Deloitte & Associates",
      auditType: "External",
      findings: 3,
      recommendations: 8,
      evidenceUrl: "#",
    },
    {
      id: 2,
      name: "Environmental Impact Assessment 2024",
      description: "Detailed assessment of environmental impacts across all operational facilities",
      referenceTo: "ISO 14001 Standards",
      uploadDate: "2024-11-20",
      status: "pending",
      auditor: "Internal Audit Team",
      auditType: "Internal",
      findings: 5,
      recommendations: 12,
      evidenceUrl: "#",
    },
    {
      id: 3,
      name: "Carbon Footprint Verification Report",
      description: "Third-party verification of carbon emissions data and reduction initiatives",
      referenceTo: "GHG Protocol",
      uploadDate: "2024-10-30",
      status: "approved",
      auditor: "Carbon Trust",
      auditType: "External",
      findings: 1,
      recommendations: 4,
      evidenceUrl: "#",
    },
    {
      id: 4,
      name: "Social Responsibility Audit Q3 2024",
      description: "Quarterly audit of social responsibility initiatives and community engagement programs",
      referenceTo: "GRI Standards",
      uploadDate: "2024-09-15",
      status: "in-review",
      auditor: "Internal Audit Team",
      auditType: "Internal",
      findings: 2,
      recommendations: 6,
      evidenceUrl: "#",
    },
  ];

  const downloadHandler = (url) => {
    window.open(url);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "approved":
        return "success";
      case "pending":
        return "warning";
      case "in-review":
        return "primary";
      case "rejected":
        return "danger";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "approved":
        return faCheckCircle;
      case "pending":
        return faClock;
      case "in-review":
        return faExclamationTriangle;
      case "rejected":
        return faExclamationTriangle;
      default:
        return faClock;
    }
  };

  useEffect(() => {
    // Simulate API call with mock data
    setTimeout(() => {
      setFilterEvidenceData(mockAuditReports);
      setIsLoading(false);
    }, 1000);
  }, [reRenderPage, orgFilterUrl]);

  const ItemPerPage = 10;
  const totalPage = Math.ceil(filterEvidenceData?.length / ItemPerPage);

  const summaryStats = [
    {
      title: "Total Reports",
      value: filterEvidenceData.length,
      icon: faFileAlt,
      color: "primary",
              bgColor: "bg-emerald-50",
      textColor: "text-primary-700",
    },
    {
      title: "Approved Reports",
      value: filterEvidenceData.filter(item => item.status === "approved").length,
      icon: faCheckCircle,
      color: "success",
      bgColor: "bg-success-50",
      textColor: "text-success-700",
    },
    {
      title: "Pending Review",
      value: filterEvidenceData.filter(item => item.status === "pending").length,
      icon: faClock,
      color: "warning",
      bgColor: "bg-yellow-50",
      textColor: "text-yellow-700",
    },
    {
      title: "External Audits",
      value: filterEvidenceData.filter(item => item.auditType === "External").length,
      icon: faBuilding,
      color: "secondary",
      bgColor: "bg-purple-50",
      textColor: "text-purple-700",
    },
  ];

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Page Header */}
      <div className="bg-white border-b border-neutral-200 px-8 py-6 shadow-sm">
        <div className="flex items-center justify-between max-w-full">
          <div>
            <h1 className="text-2xl font-semibold text-neutral-900">Audit Reports</h1>
            <p className="text-sm text-neutral-600 mt-1">Manage and review audit reports for ESG compliance and verification</p>
          </div>
          <StandardPrimaryButton
            className="px-6 font-medium"
            startContent={<FontAwesomeIcon icon={faPlus} className="text-sm" />}
            onClick={() => onOpen()}
          >
            Upload Audit Report
          </StandardPrimaryButton>
        </div>
      </div>

      <BreadCrumbsPage>
        <div className="max-w-7xl mx-auto px-8 py-8">
          <LoadingPage isLoading={isLoading} loadingText="Loading audit reports...">
            {/* Summary Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {summaryStats.map((stat, index) => (
                <Card key={index} className="border border-neutral-200 shadow-sm">
                  <CardBody className="p-4">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                        <FontAwesomeIcon 
                          icon={stat.icon} 
                          className={`text-lg ${stat.textColor}`} 
                        />
                      </div>
                      <div>
                        <p className="text-xs font-medium text-neutral-600">{stat.title}</p>
                        <p className={`text-lg font-semibold ${stat.textColor}`}>
                          {stat.value}
                        </p>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              ))}
            </div>

            {/* Audit Reports Management Section */}
            <div className="bg-white rounded-lg border border-neutral-200 shadow-sm p-6 mb-8">
              <div>
                <h2 className="text-lg font-semibold text-neutral-900">Audit Reports Management</h2>
                <p className="text-sm text-neutral-600 mt-1">
                  Upload and manage audit reports from internal and external auditors. These reports provide verification and validation of your ESG performance and compliance with relevant standards and frameworks.
                </p>
              </div>
            </div>

            {/* Audit Reports Repository Section */}
            <div className="bg-white rounded-lg border border-neutral-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-neutral-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-neutral-900">Audit Reports Repository</h3>
                    <p className="text-sm text-neutral-600 mt-1">Browse and manage uploaded audit reports and findings</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <StandardSecondaryButton
                      size="sm"
                      startContent={<FontAwesomeIcon icon={faSearch} className="text-xs" />}
                      className="border-neutral-300 hover:border-neutral-400"
                    >
                      Search
                    </StandardSecondaryButton>
                    <StandardSecondaryButton
                      size="sm"
                      startContent={<FontAwesomeIcon icon={faFilter} className="text-xs" />}
                      className="border-neutral-300 hover:border-neutral-400"
                    >
                      Filter
                    </StandardSecondaryButton>
                  </div>
                </div>
              </div>
              <div className="p-6">
                {filterEvidenceData && filterEvidenceData?.length > 0 ? (
                  <>
                    <Table
                      removeWrapper
                      aria-label="Audit reports table"
                      classNames={{
                        wrapper: "shadow-none",
                        th: "bg-neutral-50 text-neutral-700 font-semibold text-sm border-b border-neutral-200",
                        td: "border-b border-neutral-100",
                      }}
                    >
                      <TableHeader>
                        <TableColumn>Report Name</TableColumn>
                        <TableColumn>Description</TableColumn>
                        <TableColumn>Auditor</TableColumn>
                        <TableColumn>Status</TableColumn>
                        <TableColumn>Findings</TableColumn>
                        <TableColumn>Upload Date</TableColumn>
                        <TableColumn>Actions</TableColumn>
                      </TableHeader>
                      <TableBody
                        emptyContent={<NullData title="No audit reports found." />}
                      >
                        {filterEvidenceData &&
                          Array.isArray(filterEvidenceData) &&
                          filterEvidenceData.map((item, index) => (
                            <TableRow key={item.id}>
                              <TableCell>
                                <div className="flex flex-col gap-1">
                                  <div className="flex items-center gap-2">
                                    <FontAwesomeIcon 
                                      icon={faFileAlt} 
                                      className="text-neutral-500 text-sm" 
                                    />
                                    <span className="text-sm font-medium text-neutral-900">
                                      {item.name}
                                    </span>
                                  </div>
                                  <Chip size="sm" variant="flat" color="secondary">
                                    {item.auditType}
                                  </Chip>
                                </div>
                              </TableCell>
                              <TableCell>
                                <span className="text-sm text-neutral-600 max-w-xs truncate block">
                                  {item.description}
                                </span>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <FontAwesomeIcon 
                                    icon={item.auditType === "External" ? faBuilding : faUser} 
                                    className="text-neutral-500 text-xs" 
                                  />
                                  <span className="text-sm text-neutral-600">{item.auditor}</span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size="sm"
                                  variant="flat"
                                  color={getStatusColor(item.status)}
                                  startContent={<FontAwesomeIcon icon={getStatusIcon(item.status)} className="text-xs" />}
                                >
                                  {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                                </Chip>
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-col gap-1">
                                  <span className="text-sm text-neutral-600">
                                    {item.findings} findings
                                  </span>
                                  <span className="text-xs text-neutral-500">
                                    {item.recommendations} recommendations
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <FontAwesomeIcon 
                                    icon={faCalendar} 
                                    className="text-neutral-500 text-xs" 
                                  />
                                  <span className="text-sm text-neutral-600">
                                    {new Date(item.uploadDate).toLocaleDateString()}
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <StandardSecondaryButton
                                    size="sm"
                                    className="text-neutral-600 hover:text-neutral-900"
                                    startContent={<FontAwesomeIcon icon={faEye} className="text-xs" />}
                                  >
                                    View
                                  </StandardSecondaryButton>
                                  <StandardSecondaryButton
                                    size="sm"
                                    className="text-neutral-600 hover:text-neutral-900"
                                    startContent={<FontAwesomeIcon icon={faDownload} className="text-xs" />}
                                    onClick={() => downloadHandler(item.evidenceUrl)}
                                  >
                                    Download
                                  </StandardSecondaryButton>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                    {filterEvidenceData?.length > 0 && (
                      <div className="w-full mt-8 flex items-center justify-center">
                        <Pagination
                          showControls
                          total={totalPage}
                          color="secondary"
                          page={currentPage}
                          onChange={setCurrentPage}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <NullData />
                )}
              </div>
            </div>
          </LoadingPage>
        </div>
      </BreadCrumbsPage>

      <AddEvidenceModal
        onSubmitHandler={1}
        id={1}
        isOpen={isOpen}
        onOpen={onOpen}
        onOpenChange={onOpenChange}
        setReRenderPage={setReRenderPage}
        pageContext="Audit Reports"
      />
    </div>
  );
};

export default AuditReports; 